import {
  Component,
  Input,
  Output,
  EventEmitter
} from '@angular/core';

import { CommonModule } from '@angular/common';

export interface GalleryItem {
  type: 'image' | 'video';
  src: string;
  thumbnail?: string;
}

@Component({
  selector: 'app-gallery',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './gallery.component.html'
})
export class GalleryComponent {

  /* ================================================================
     INPUT
  ================================================================ */

  private _images: GalleryItem[] = [];

  @Input()
  set images(value: any[] | null | undefined) {

    if (!Array.isArray(value)) {
      this._images = [];
      this.prepareImages();
      return;
    }

    this._images = value
      .map((item: any) => {

        /*
         * Already formatted GalleryItem
         */
        if (
          item?.type === 'image' ||
          item?.type === 'video'
        ) {
          if (typeof item.src !== 'string' || !item.src.trim()) return null;
          return {
            type: item.type,
            src: item.src,
            thumbnail: item.thumbnail
          } as GalleryItem;
        }

        /*
         * Raw URL / API object
         */
        const url =
          typeof item === 'string'
            ? item
            : item?.videoUrl ||
              item?.src ||
              item?.url;

        if (typeof url !== 'string' || !url.trim()) {
          return null;
        }

        const isVideo = this.isVideo(url);

        return {
          type: isVideo ? 'video' : 'image',
          src: url,
          thumbnail: isVideo
            ? `${url}#t=0.1`
            : url
        } as GalleryItem;

      })
      .filter(Boolean) as GalleryItem[];

    this.prepareImages();
  }

  get images(): GalleryItem[] {
    return this._images;
  }

  onImageError(event: Event): void {
    const image = event.target as HTMLImageElement;
    image.onerror = null;
    if (image.getAttribute('src') === 'assets/img/property-placeholder.svg') return;
    image.src = 'assets/img/property-placeholder.svg';
    image.alt = 'Property photo unavailable';
  }


  /* ================================================================
     LIGHTBOX EVENT

     Gallery does NOT own the lightbox anymore.

     It only tells the parent:
     "Open image at this index."
  ================================================================ */

  @Output()
  lightboxOpen = new EventEmitter<number>();


  /* ================================================================
     DERIVED ARRAYS
  ================================================================ */

  desktopImages: GalleryItem[] = [];

  mobileImages: GalleryItem[] = [];


  private prepareImages(): void {

    if (!this._images.length) {
      this.desktopImages = [];
      this.mobileImages = [];
      return;
    }

    this.desktopImages = this._images.slice(0, 5);

    this.mobileImages = this._images.slice(0, 3);
  }


  /* ================================================================
     OPEN LIGHTBOX

     IMPORTANT:
     No local lightbox state.
     No body scroll manipulation here.
  ================================================================ */

  openLightbox(index: number): void {

    if (
      !this.images.length ||
      index < 0 ||
      index >= this.images.length
    ) {
      return;
    }

    this.lightboxOpen.emit(index);
  }


  /* ================================================================
     COUNTS
  ================================================================ */

  get remainingCount(): number {

    return Math.max(
      this.images.length - 4,
      0
    );
  }


  get remainingCountMobile(): number {

    return Math.max(
      this.images.length - 3,
      0
    );
  }


  /* ================================================================
     VIDEO DETECTION
  ================================================================ */

  private isVideo(url: string): boolean {

    if (!url) {
      return false;
    }

    const cleanUrl = url
      .split('?')[0]
      .split('#')[0]
      .toLowerCase();

    return (
      cleanUrl.endsWith('.mp4') ||
      cleanUrl.endsWith('.webm') ||
      cleanUrl.endsWith('.ogg') ||
      cleanUrl.endsWith('.mov')
    );
  }

}
